/**
 * Feedback FAB + modal — bottom-right "Send feedback" affordance.
 *
 * Public API:
 *   mountFeedbackFab({ getJobId, getCurrentFile, captureScreenshot }) → detach()
 *   submitFeedback({ comment, screenshot, attachment, jobId }) → Promise<Response>
 *
 * The FAB is fixed at the bottom-right of the editor route, brand-green.
 * Clicking opens a centered modal with a comment textarea and two toggles
 * (capture screenshot, attach current file). Clicking "Send feedback"
 * POSTs multipart/form-data to /api/feedback (existing endpoint exposed
 * by web/server.py:submit_feedback).
 *
 * The screenshot is best-effort: if `html2canvas` is not present, the
 * toggle is hidden and feedback is sent without a screenshot. We do NOT
 * pull in html2canvas as a hard dep — the brief says "no new frontend
 * libraries". A future PR can lazy-load it.
 *
 * @module feedback
 */
/**
 * Mount the FAB and return a detach() handle.
 */
export function mountFeedbackFab(opts = {}) {
    const { getJobId, getCurrentFile, captureScreenshot, onToast } = opts;
    if (typeof document === "undefined")
        return { detach() { } };
    // FAB
    const fab = document.createElement("button");
    fab.id = "editor-feedback-fab";
    fab.type = "button";
    fab.className = "editor-feedback-fab";
    // WCAG 2.5.3: aria-label must contain the visible text as a substring.
    // Visible text is "💬 Feedback"; aria-label "💬 Feedback" satisfies the rule.
    fab.setAttribute("aria-label", "💬 Feedback");
    fab.title = "Send feedback";
    fab.textContent = "💬 Feedback";
    document.body.appendChild(fab);
    // Modal
    const backdrop = document.createElement("div");
    backdrop.className = "editor-feedback-backdrop";
    backdrop.setAttribute("aria-hidden", "true");
    const modal = document.createElement("div");
    modal.className = "editor-feedback-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-label", "Send feedback");
    // Build modal DOM imperatively (innerHTML doesn't parse in unit-test
    // stubs; querySelector on a parsed DOM works in browsers either way).
    const title = document.createElement("div");
    title.className = "editor-feedback-modal-title";
    title.textContent = "Send feedback";
    modal.appendChild(title);
    const subtitle = document.createElement("div");
    subtitle.className = "editor-feedback-modal-subtitle";
    subtitle.textContent = "What did you notice? Optional details welcome.";
    modal.appendChild(subtitle);
    const ta = document.createElement("textarea");
    ta.className = "editor-feedback-modal-textarea";
    ta.setAttribute("rows", "6");
    ta.setAttribute("placeholder", "Describe what you saw, what you expected, or any suggestion. Screenshot & current file will be attached if checked below.");
    modal.appendChild(ta);
    const toggles = document.createElement("div");
    toggles.className = "editor-feedback-modal-toggles";
    modal.appendChild(toggles);
    const rowShot = document.createElement("label");
    rowShot.className = "editor-feedback-toggle-row";
    rowShot.dataset.role = "screenshot-row";
    const cbShot = document.createElement("input");
    cbShot.type = "checkbox";
    cbShot.className = "editor-feedback-toggle-screenshot";
    cbShot.checked = true;
    rowShot.appendChild(cbShot);
    const tShotLbl = document.createElement("span");
    tShotLbl.className = "editor-feedback-toggle-text";
    const tShotName = document.createElement("span");
    tShotName.className = "editor-feedback-toggle-label";
    tShotName.textContent = "Capture screenshot";
    tShotLbl.appendChild(tShotName);
    const tShotDesc = document.createElement("span");
    tShotDesc.className = "editor-feedback-toggle-desc";
    tShotDesc.textContent = "Includes the editor view at submit time.";
    tShotLbl.appendChild(tShotDesc);
    rowShot.appendChild(tShotLbl);
    toggles.appendChild(rowShot);
    const rowFile = document.createElement("label");
    rowFile.className = "editor-feedback-toggle-row";
    rowFile.dataset.role = "attachment-row";
    const cbFile = document.createElement("input");
    cbFile.type = "checkbox";
    cbFile.className = "editor-feedback-toggle-attach";
    rowFile.appendChild(cbFile);
    const tFileLbl = document.createElement("span");
    tFileLbl.className = "editor-feedback-toggle-text";
    const tFileName = document.createElement("span");
    tFileName.className = "editor-feedback-toggle-label";
    tFileName.textContent = "Attach current file";
    tFileLbl.appendChild(tFileName);
    const tFileDesc = document.createElement("span");
    tFileDesc.className = "editor-feedback-toggle-desc";
    tFileDesc.textContent = "Lets us reproduce the issue locally.";
    tFileLbl.appendChild(tFileDesc);
    rowFile.appendChild(tFileLbl);
    toggles.appendChild(rowFile);
    const actions = document.createElement("div");
    actions.className = "editor-feedback-modal-actions";
    modal.appendChild(actions);
    const btnCanc = document.createElement("button");
    btnCanc.type = "button";
    btnCanc.className = "editor-feedback-modal-cancel";
    btnCanc.textContent = "Cancel";
    actions.appendChild(btnCanc);
    const btnSend = document.createElement("button");
    btnSend.type = "button";
    btnSend.className = "editor-feedback-modal-send";
    btnSend.textContent = "Send feedback";
    actions.appendChild(btnSend);
    const errEl = document.createElement("div");
    errEl.className = "editor-feedback-modal-error";
    errEl.setAttribute("role", "alert");
    errEl.hidden = true;
    modal.appendChild(errEl);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    const win = typeof window !== "undefined"
        ? window
        : undefined;
    // Hide the screenshot row if no capture path is available.
    const hasScreenshot = typeof captureScreenshot === "function"
        || (win != null && typeof win.html2canvas === "function");
    if (!hasScreenshot) {
        const row = modal.querySelector("[data-role=screenshot-row]");
        if (row)
            row.style.display = "none";
        cbShot.checked = false;
    }
    // Hide attachment row if no current file is available.
    if (typeof getCurrentFile !== "function") {
        const row = modal.querySelector("[data-role=attachment-row]");
        if (row)
            row.style.display = "none";
    }
    function open() {
        backdrop.classList.add("is-open");
        backdrop.setAttribute("aria-hidden", "false");
        ta.value = "";
        errEl.hidden = true;
        errEl.textContent = "";
        setTimeout(() => ta.focus(), 0);
    }
    function close() {
        backdrop.classList.remove("is-open");
        backdrop.setAttribute("aria-hidden", "true");
    }
    fab.addEventListener("click", open);
    btnCanc.addEventListener("click", close);
    backdrop.addEventListener("click", (ev) => {
        if (ev.target === backdrop)
            close();
    });
    document.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape" && backdrop.classList.contains("is-open"))
            close();
    });
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- intentional async handler; errors caught inside
    btnSend.addEventListener("click", async () => {
        btnSend.disabled = true;
        btnSend.textContent = "Sending…";
        errEl.hidden = true;
        try {
            let screenshot = null;
            if (cbShot.checked) {
                if (typeof captureScreenshot === "function") {
                    screenshot = await captureScreenshot();
                }
                else if (win != null && typeof win.html2canvas === "function") {
                    const canvas = await win.html2canvas(document.documentElement);
                    screenshot = await new Promise((res) => canvas.toBlob(res, "image/png"));
                }
            }
            let attachment = null;
            if (cbFile.checked && typeof getCurrentFile === "function") {
                attachment = await getCurrentFile();
            }
            const jobId = typeof getJobId === "function" ? (getJobId() || "") : "";
            await submitFeedback({
                comment: ta.value || "",
                screenshot,
                attachment,
                jobId,
            });
            close();
            if (typeof onToast === "function")
                onToast("Thanks for the feedback!");
        }
        catch (err) {
            errEl.hidden = false;
            errEl.textContent = `Couldn't send — ${err instanceof Error ? err.message : "try again"}.`;
        }
        finally {
            btnSend.disabled = false;
            btnSend.textContent = "Send feedback";
        }
    });
    return {
        detach() {
            try {
                fab.remove();
            }
            catch { /* DOM might already be torn down */ }
            try {
                backdrop.remove();
            }
            catch { /* same */ }
        },
        open,
        close,
        // Test accessors:
        _modal: modal,
        _fab: fab,
    };
}
/**
 * POST a feedback submission to /api/feedback.
 *
 * The endpoint expects multipart/form-data with fields:
 *   message    — the comment text (required)
 *   contact    — optional name or email (we leave blank for editor-side feedback)
 *   job_id     — optional editor job id
 *   attachment — optional binary file (PDF or screenshot)
 *
 * We only attach ONE file (the endpoint accepts a single attachment).
 * Priority: PDF attachment > screenshot. The other is encoded into the
 * message text as a note so the admin sees it was offered.
 */
export async function submitFeedback({ comment, screenshot, attachment, jobId } = {}) {
    if (typeof FormData === "undefined" || typeof fetch === "undefined") {
        throw new Error("FormData/fetch unavailable in this environment");
    }
    const fd = new FormData();
    let messageText = comment || "(no comment)";
    if (screenshot && attachment) {
        messageText += "\n\n[editor: screenshot captured but only attaching current file; screenshot omitted]";
    }
    fd.set("message", messageText);
    if (jobId)
        fd.set("job_id", jobId);
    fd.set("contact", ""); // editor-side feedback is anonymous by default
    if (attachment) {
        const name = (attachment instanceof File ? attachment.name : null) || "editor-attachment.pdf";
        fd.set("attachment", attachment, name);
    }
    else if (screenshot) {
        const name = "editor-screenshot.png";
        fd.set("attachment", screenshot, name);
    }
    const resp = await fetch("/api/feedback", { method: "POST", body: fd });
    if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status}${text ? `: ${text.slice(0, 80)}` : ""}`);
    }
    return resp;
}
